// src/components/modals/LogMacrosModal.tsx
import { useState } from "react";
import { X } from "lucide-react";
import { getSupabase, currentUser } from "@/lib/supabase";

export default function LogMacrosModal({ open, onClose, onSuccess }: { 
  open: boolean; 
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const supabase = getSupabase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData);

    const user = await currentUser();
    if (!user) return;

    const { error } = await supabase.from("daily_calorie_macro_logs").insert({
      user_id: user.id,
      meal_name: data.meal_name as string,
      daily_total_calories: data.calories ? Number(data.calories) : 0,
      daily_total_protein_grams: data.protein ? Number(data.protein) : 0,
      daily_total_carb_grams: data.carbs ? Number(data.carbs) : 0,
      daily_total_fat_grams: data.fat ? Number(data.fat) : 0,
      created_date: new Date().toISOString(),
    });

    setLoading(false);
    if (error) {
      alert("Error: " + error.message);
    } else {
      onClose();
      onSuccess();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Log macros</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 block mb-1">Meal name</label>
            <input name="meal_name" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a] placeholder-gray-600" placeholder="Chicken & Rice" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Protein (g)</label>
            <input name="protein" type="number" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a] placeholder-gray-600" placeholder="50" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Carbs (g)</label>
            <input name="carbs" type="number" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a] placeholder-gray-600" placeholder="80" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Fat (g)</label>
            <input name="fat" type="number" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a] placeholder-gray-600" placeholder="30" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Calories</label>
            <input name="calories" type="number" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a] placeholder-gray-600" placeholder="700" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 bg-[#f1f5f9] py-2 rounded-lg text-[#0f172a] font-medium">Close</button>
          <button type="submit" disabled={loading} className="flex-1 bg-green-500 text-[#0f172a] py-2 rounded-lg font-medium disabled:opacity-60">
            {loading ? "Saving..." : "Log macros"}
          </button>
        </div>
      </form>
    </div>
  );
}
